import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRoute } from '@react-navigation/native';
import { InteractionManager, Linking, Platform, StatusBar } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShallow } from 'zustand/shallow';

import { Stack } from '@/components/layout';
import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { auth } from '@/features/auth/types';
import {
  useGuardianProfileQuery,
  useUpdateNotificationPreferencesMutation,
} from '@/features/guardian/queries';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  GuardianProfileActionHeader,
  NotificationPreferencesContent,
  NOTIFICATION_PREFERENCE_ITEMS,
  type NotificationPreferenceKey,
} from '@/features/guardian-profile/components';
import {
  createStyles,
  useGuardianStackNavigation,
  useNotificationPermission,
  useScaling,
} from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useSessionStore, useUiStore } from '@/stores';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianProfile.notificationPreferences;

type GuardianNotificationPreferencesContentProps = {
  tabletPanel?: boolean;
  notificationCenterTablet?: boolean;
  onSaved?: () => void;
  onSaveDisabledChange?: (disabled: boolean) => void;
};

export type GuardianNotificationPreferencesContentHandle = {
  save: () => Promise<void>;
};

function buildPreferences(
  preferences?: Partial<auth.NotificationPreferences> | null,
): auth.NotificationPreferences {
  return {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...(preferences ?? {}),
  };
}

function visiblePreferencesEqual(
  a: auth.NotificationPreferences,
  b: auth.NotificationPreferences,
): boolean {
  return NOTIFICATION_PREFERENCE_ITEMS.every(item => a[item.key] === b[item.key]);
}

export const GuardianNotificationPreferencesContent = forwardRef<
  GuardianNotificationPreferencesContentHandle,
  GuardianNotificationPreferencesContentProps
>(function GuardianNotificationPreferencesContent(
  { tabletPanel = false, notificationCenterTablet = false, onSaved, onSaveDisabledChange },
  ref,
) {
  const uid = useSessionStore(state => state.user?.uid);
  const { data: profile } = useGuardianProfileQuery(uid);
  const updatePreferences = useUpdateNotificationPreferencesMutation(uid);
  const {
    isDenied,
    isUndetermined,
    canAsk: canAskNotificationPermission,
    isSupported: isNotificationSupported,
    refresh: refreshNotificationPermission,
    request: requestNotificationPermission,
  } = useNotificationPermission();
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(state => ({
      showGlobalLoading: state.showGlobalLoading,
      hideGlobalLoading: state.hideGlobalLoading,
    })),
  );
  const requestedPermissionRef = useRef(false);

  const serverPreferences = useMemo(
    () => buildPreferences(profile?.notificationPreferences),
    [profile?.notificationPreferences],
  );
  const [preferences, setPreferences] = useState<auth.NotificationPreferences>(serverPreferences);
  const previousServerPreferencesRef = useRef(serverPreferences);
  const isDirty = !visiblePreferencesEqual(preferences, serverPreferences);
  const saveDisabled = !isDirty || updatePreferences.isPending;
  const isWeb = Platform.OS === 'web';
  // Web shows the banner while push is still actionable: the guardian hasn't been asked yet,
  // or they blocked it and can turn it back on. A browser with no Notification API is hidden
  // entirely — there is nothing the guardian could do about it. Native only needs the denied
  // case, since an undetermined status is resolved by the prompt below.
  const showPermissionBanner = isWeb
    ? isNotificationSupported && (isUndetermined || isDenied)
    : isDenied;

  useEffect(() => {
    const previousServerPreferences = previousServerPreferencesRef.current;
    previousServerPreferencesRef.current = serverPreferences;

    setPreferences(currentPreferences =>
      visiblePreferencesEqual(currentPreferences, previousServerPreferences)
        ? serverPreferences
        : currentPreferences,
    );
  }, [serverPreferences]);

  useEffect(() => {
    onSaveDisabledChange?.(saveDisabled);
  }, [onSaveDisabledChange, saveDisabled]);

  useEffect(() => {
    if (isWeb) return undefined;

    let cancelled = false;
    const task = InteractionManager.runAfterInteractions(() => {
      if (requestedPermissionRef.current) return;
      requestedPermissionRef.current = true;

      refreshNotificationPermission().then(status => {
        if (!cancelled && status === 'undetermined') {
          requestNotificationPermission();
        }
      });
    });

    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [isWeb, refreshNotificationPermission, requestNotificationPermission]);

  const setPreference = useCallback(
    (key: NotificationPreferenceKey, value: boolean) => {
      setPreferences(prev => ({ ...prev, [key]: value }));

      // Switching a preference on is the moment the guardian actually asks for notifications,
      // which makes it the best time to request the permission — and the toggle is a real user
      // gesture, so the browser will honour the prompt. Guarded by `canAsk`, so this never
      // nags someone who already decided (on native the prompt has run on mount, making it a
      // no-op there). The preference itself is independent of the permission: it controls
      // whether the notification is raised at all, in-app included, so a declined prompt
      // leaves the toggle on.
      if (value && canAskNotificationPermission) requestNotificationPermission();
    },
    [canAskNotificationPermission, requestNotificationPermission],
  );

  const handlePermissionBannerPress = useCallback(() => {
    // Native: the OS has a settings page for this, so send them straight there.
    if (!isWeb) {
      Linking.openSettings();
      return;
    }

    // Web, not asked yet: this runs straight off the banner's click handler, which is what
    // keeps the browser's user-gesture requirement satisfied. A browser with no Notification
    // API never reaches here (the banner is hidden), and `request` no-ops there anyway.
    if (canAskNotificationPermission) {
      requestNotificationPermission();
      return;
    }

    // Web, blocked: browsers deliberately expose no API for opening their own site settings,
    // and they won't re-prompt once denied — so point the guardian at their browser settings.
    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.webNotificationsDeniedMessage,
      buttons: [{ text: STRINGS.common.buttons.dismiss }],
    });
  }, [isWeb, canAskNotificationPermission, requestNotificationPermission]);

  const handleSave = useCallback(async () => {
    if (saveDisabled) return;

    try {
      showGlobalLoading();
      await updatePreferences.mutateAsync({
        ...serverPreferences,
        ...preferences,
      });
      onSaved?.();
    } finally {
      hideGlobalLoading();
    }
  }, [
    hideGlobalLoading,
    onSaved,
    preferences,
    saveDisabled,
    serverPreferences,
    showGlobalLoading,
    updatePreferences,
  ]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  return (
    <NotificationPreferencesContent
      tabletPanel={tabletPanel}
      notificationCenterTablet={notificationCenterTablet}
      preferences={preferences}
      showPermissionBanner={showPermissionBanner}
      permissionBannerMessage={strings.warning}
      isDirty={isDirty}
      saving={updatePreferences.isPending}
      onSave={handleSave}
      onPermissionBannerPress={handlePermissionBannerPress}
      onPreferenceChange={setPreference}
    />
  );
});

export function GuardianNotificationPreferencesScreen() {
  const { isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'GuardianNotificationPreferences'>['route']>();
  const contentRef = useRef<GuardianNotificationPreferencesContentHandle>(null);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const notificationCenterTablet = isTablet && route.params?.source === 'notification-center';

  const handleSaved = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleSave = useCallback(() => {
    contentRef.current?.save();
  }, []);

  const content = (
    <>
      <StatusBar
        barStyle="dark-content"
        backgroundColor={isTablet ? colors['0'] : guardianColors['1']}
      />
      <GuardianProfileActionHeader
        title={strings.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={navigation.goBack}
        rightAction={{
          label: strings.save,
          onPress: handleSave,
          disabled: saveDisabled,
          showOnTablet: notificationCenterTablet,
        }}
      />
      <GuardianNotificationPreferencesContent
        ref={contentRef}
        tabletPanel={isTablet && !notificationCenterTablet}
        notificationCenterTablet={notificationCenterTablet}
        onSaved={handleSaved}
        onSaveDisabledChange={setSaveDisabled}
      />
    </>
  );

  if (notificationCenterTablet) {
    return (
      <LinearGradient
        colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
        locations={[0.08, 0.47, 0.9]}
        useAngle
        angle={191.6}
        style={styles.gradient}>
        {content}
      </LinearGradient>
    );
  }

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      {content}
    </Stack>
  );
}

export default GuardianNotificationPreferencesScreen;

const useStyles = createStyles(() => ({
  gradient: { flex: 1 },
}));
